import { useState } from 'react'
import type { Campaign, CampaignMode, PlatformConfig } from '@/api/client'
import { useTargets } from '@/lib/queries'
import { Button, Drawer, ErrorState, Field, FieldGroup, Input, PlatformMark, Select, Textarea, platformName, useToast } from '@/ui'
import {
  ACCOUNT_FLOOR_MIN,
  MODES,
  durationText,
  hasChannels,
  isHHMM,
  rulesToText,
  selectableModes,
  spacing,
  targetsFor,
  textToRules,
  useCampaignMutations,
} from './shared'

/** The platforms the campaign posts to. A campaign with none yet is given
 *  Reddit, as the old editor did. */
function platformsOf(c: Campaign): string[] {
  const ps = Object.keys(c.PlatformConfigs ?? {})
  return ps.length > 0 ? ps : ['reddit']
}

/** The gap fields are kept as typed text, so an emptied field is told apart
 *  from a 0 and neither turns into a default behind the operator's back. */
interface PlatformDraft {
  targetId: number
  cooldown: string
  banned: string
}

function draftsOf(c: Campaign): Record<string, PlatformDraft> {
  return Object.fromEntries(
    platformsOf(c).map((p) => {
      const cfg: PlatformConfig = c.PlatformConfigs?.[p] ?? { enabled: true }
      return [
        p,
        {
          targetId: cfg.target_id ?? 0,
          cooldown: String(cfg.channel_cooldown_hours || 24),
          banned: rulesToText(cfg.channel_rules),
        },
      ]
    }),
  )
}

const MIN_GAP = 0.5

/** The first thing wrong with the form, in the words the server uses too, or
 *  null. Checked before sending so the operator sees it with the input still
 *  in front of them. */
export function pacingError(f: {
  postsPerDay: number
  start: string
  end: string
  tz: string
  per: Record<string, PlatformDraft>
}): string | null {
  if (!Number.isInteger(f.postsPerDay) || f.postsPerDay < 1 || f.postsPerDay > 24) return '하루 글 수는 1에서 24 사이로 적는다'
  if ((f.start && !isHHMM(f.start)) || (f.end && !isHHMM(f.end))) return `게시 창은 시:분 형식으로 적는다 (예: 09:00)`
  if (!!f.start !== !!f.end) return '게시 창은 시작과 끝을 함께 적거나 둘 다 비운다'
  if (f.start && f.start >= f.end) return '게시 창의 시작은 끝보다 앞서야 한다'
  try {
    new Intl.DateTimeFormat('en', { timeZone: f.tz })
  } catch {
    return `시간대 "${f.tz}"를 찾을 수 없다 (예: Asia/Seoul)`
  }
  for (const [p, d] of Object.entries(f.per)) {
    // Zero is not "no gap": the server reads it as unset and uses its own
    // default, which is how a typed 0 used to become 24 hours silently.
    const name = platformName(p)
    if (hasChannels(p) && !(Number(d.cooldown) >= MIN_GAP)) return `${name}의 채널 간격은 ${MIN_GAP}시간 이상으로 적는다`
  }
  return null
}

/** Pacing: when an approved draft goes out. Approving reads these to pick the
 *  slot — inside the window, up to the day's limit, with the account's and the
 *  channel's gaps kept, at an off-the-hour minute. Posts already scheduled
 *  keep the slot they were given; after a save the operator is offered the
 *  re-slot (`onSaved`, which the page answers by closing this and asking).
 *
 *  A drawer over the campaign page, mounted only while open so each opening
 *  starts from the saved values. Mount with `key={campaign.ID}`, not the
 *  update time: a remount on every save is what used to wipe the form after
 *  an error. */
export function PacingEditor({ campaign, onClose, onSaved }: { campaign: Campaign; onClose: () => void; onSaved: () => void }) {
  const targets = useTargets().data ?? []
  const toast = useToast()
  const { pacing } = useCampaignMutations(campaign.ID)
  const [mode, setMode] = useState<CampaignMode>(campaign.Mode)
  const [postsPerDay, setPostsPerDay] = useState(campaign.PostsPerDay || 1)
  const [start, setStart] = useState(campaign.PostingWindowStart ?? '')
  const [end, setEnd] = useState(campaign.PostingWindowEnd ?? '')
  const [tz, setTz] = useState(campaign.Timezone ?? 'Asia/Seoul')
  const [per, setPer] = useState(() => draftsOf(campaign))
  const [autoApprove, setAutoApprove] = useState(!!campaign.AutoApprove)
  const [problem, setProblem] = useState<string | null>(null)

  const setPlat = (p: string, patch: Partial<PlatformDraft>) => {
    setProblem(null)
    setPer((prev) => ({ ...prev, [p]: { ...prev[p], ...patch } }))
  }

  // The spacing between the campaign's posts follows from these two fields;
  // there is no setting for it, so raising the posts per day needs nothing
  // else changed.
  const space = spacing(postsPerDay, start, end)
  const floor = Math.min(space.minutes, ACCOUNT_FLOOR_MIN)

  const save = async () => {
    const found = pacingError({ postsPerDay, start, end, tz, per })
    setProblem(found)
    if (found) return
    const configs = { ...(campaign.PlatformConfigs ?? {}) }
    for (const [p, d] of Object.entries(per)) {
      // min_gap_hours is no longer read; a saved form drops the old value.
      const { min_gap_hours: _unread, ...cfg }: PlatformConfig = configs[p] ?? { enabled: true }
      configs[p] = {
        ...cfg,
        enabled: cfg.enabled ?? true,
        target_id: d.targetId,
        // A platform without channels has no channel gap to set; its stored
        // value is left as it was.
        ...(hasChannels(p) ? { channel_cooldown_hours: Number(d.cooldown) } : {}),
        channel_rules: textToRules(d.banned, cfg.channel_rules),
      }
    }
    try {
      await pacing.mutateAsync({
        id: campaign.ID,
        pacing: {
          mode,
          posts_per_day: postsPerDay,
          posting_window_start: start,
          posting_window_end: end,
          timezone: tz,
          platform_configs: configs,
          // A Coupang campaign's switch is in its 쿠팡 글감 settings.
          ...(campaign.Mode !== 'affiliate' ? { auto_approve: autoApprove } : {}),
        },
      })
    } catch {
      return // the message shows below, the form keeps what was typed
    }
    toast.success('페이싱을 저장했다')
    // Posts already scheduled keep their old slots; the page offers to move them.
    onSaved()
  }

  return (
    <Drawer
      open
      onClose={onClose}
      title="페이싱 편집"
      meta={campaign.Name}
      footer={
        <div className="ml-auto flex gap-2">
          <Button variant="ghost" onClick={onClose}>
            취소
          </Button>
          <Button variant="primary" loading={pacing.isPending} onClick={() => void save()}>
            페이싱 저장
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <p className="text-table text-muted-foreground">
          승인한 초안은 이 값으로 게시 시각을 받는다. 게시 창 안에서, 하루 한도까지 창 안에 고르게, 같은 서브레딧의 간격을 지켜
          정시가 아닌 시각으로 잡는다. 이미 잡힌 예약은 따라 바뀌지 않아서, 저장한 뒤 다시 배치할지 묻는다.
        </p>

        {campaign.Mode !== 'affiliate' && (
          <Field label="모드" hint={MODES.find((m) => m.value === mode)?.note}>
            <Select value={mode} onChange={(e) => setMode(e.target.value as CampaignMode)}>
              {selectableModes(campaign.Mode).map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <FieldGroup title="게시 시각" columns={2}>
          <Field label="하루 글 수">
            <Input
              type="number"
              min={1}
              max={24}
              value={postsPerDay}
              onChange={(e) => {
                setProblem(null)
                setPostsPerDay(Number(e.target.value))
              }}
            />
          </Field>
          <Field label="시간대">
            <Input
              value={tz}
              onChange={(e) => {
                setProblem(null)
                setTz(e.target.value)
              }}
            />
          </Field>
          <Field label="게시 창 시작">
            <Input
              value={start}
              onChange={(e) => {
                setProblem(null)
                setStart(e.target.value)
              }}
              placeholder="09:00"
            />
          </Field>
          <Field label="게시 창 끝">
            <Input
              value={end}
              onChange={(e) => {
                setProblem(null)
                setEnd(e.target.value)
              }}
              placeholder="22:00"
            />
          </Field>
        </FieldGroup>
        <p
          className={space.fits ? 'rounded-md bg-muted/60 px-3 py-2 text-table text-muted-foreground' : 'rounded-md bg-status-failed-soft px-3 py-2 text-table text-status-failed'}
          data-testid="spacing"
        >
          {postsPerDay > 1 ? (
            <>
              글 사이 간격은 <b className="tabular-nums text-foreground">{durationText(space.minutes)}</b> — 하루 글 수와 게시 창에
              맞춰 자동으로 정해진다.
            </>
          ) : (
            '하루 한 개라 글 사이 간격은 없다.'
          )}{' '}
          같은 계정의 다른 캠페인·리딤 글과는 {durationText(floor)} 이상 띄운다.
          {!space.fits && ` 하루 ${postsPerDay}개는 이 게시 창에 다 들어가지 않는다 — 창을 넓히거나 글 수를 줄인다.`}
        </p>

        {campaign.Mode !== 'affiliate' && (
          <div className="space-y-1">
            <label className="flex items-center gap-2 text-body">
              <input
                type="checkbox"
                className="h-4 w-4 shrink-0 accent-[rgb(var(--accent))]"
                checked={autoApprove}
                onChange={(e) => setAutoApprove(e.target.checked)}
              />
              자동 승인
            </label>
            <p className="text-meta text-muted-foreground">
              검토 대기에 들어온 초안을 10분마다 먼저 쓴 것부터 승인해, 위 규칙대로 다음 빈 자리에 예약한다. 30일 안에 자리가 없는
              초안은 검토 대기에 남는다. 예약된 글을 검토 대기로 되돌리면 다시 자동 승인하지 않는다.
            </p>
          </div>
        )}

        {Object.entries(per).map(([p, d]) => (
          <fieldset key={p} className="min-w-0 space-y-3">
            <legend className="inline-flex items-center gap-1.5 text-section font-semibold">
              <PlatformMark platform={p} withName />
            </legend>
            <Field label="글을 올릴 계정" hint="초안마다 따로 지정하지 않아도 여기 값이 쓰인다.">
              <Select value={String(d.targetId)} onChange={(e) => setPlat(p, { targetId: Number(e.target.value) })}>
                <option value="0">— 지정 안 함 (게시 불가) —</option>
                {targetsFor(p, targets, d.targetId).map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.name} ({platformName(t.platform)})
                  </option>
                ))}
              </Select>
            </Field>
            {hasChannels(p) && (
              <>
                <Field label="채널 간격 (시간)" hint="같은 서브레딧에 다시 올리기까지.">
                  <Input type="number" min={MIN_GAP} step={1} value={d.cooldown} onChange={(e) => setPlat(p, { cooldown: e.target.value })} />
                </Field>
                <Field label="채널별 금지 문구" hint="한 줄에 “서브레딧: 문구”. 서브레딧이 쓰는 문구를 막는다.">
                  <Textarea
                    rows={3}
                    value={d.banned}
                    onChange={(e) => setPlat(p, { banned: e.target.value })}
                    className="font-mono text-meta"
                    placeholder="AppGiveaway: DM you a lifetime-unlock code"
                  />
                </Field>
              </>
            )}
          </fieldset>
        ))}

        <p className="text-meta text-muted-foreground">
          {campaign.Mode !== 'affiliate' && (
            <>
              모드를 <b>수동</b>으로 두면 생성기가 글을 만들지 않는다.{' '}
            </>
          )}
          캠페인은 가동 상태로 둬야 올라간 글의 댓글·반응이 계속 수집된다.
        </p>

        {problem != null && (
          <p role="alert" className="text-table text-status-failed">
            {problem}
          </p>
        )}
        {problem == null && pacing.error != null && <ErrorState title="저장하지 못했다" error={pacing.error} />}
      </div>
    </Drawer>
  )
}
